import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useStripeConnectStatus } from "../hooks/useStripeConnect";
import { API } from "../utils/api";
import { toast } from "sonner";
import {
  CreditCard,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Loader2,
  ExternalLink,
  Shield,
  Wallet,
  Percent,
} from "lucide-react";

export default function HostPayments() {
  const { token } = useAuth();
  const { status, loading, refresh } = useStripeConnectStatus(token);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Al volver del flujo de Stripe, refrescamos el status automáticamente
  useEffect(() => {
    if (searchParams.get("completed") === "1" || searchParams.get("refresh") === "1") {
      (async () => {
        if (!token) return;
        try {
          setActionLoading(true);
          await axios.post(`${API}/stripe/connect/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
          await refresh();
          toast.success("Estado actualizado");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error refrescando estado");
        } finally {
          setActionLoading(false);
        }
      })();
      searchParams.delete("completed");
      searchParams.delete("refresh");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, token, refresh]);

  const refreshFromStripe = async () => {
    if (!token) return;
    try {
      setActionLoading(true);
      await axios.post(`${API}/stripe/connect/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error refrescando estado");
    } finally {
      setActionLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      setActionLoading(true);
      const res = await axios.post(
        `${API}/stripe/connect/start`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando onboarding");
      setActionLoading(false);
    }
  };

  const openDashboard = async () => {
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/stripe/connect/dashboard-link`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.open(res.data.dashboard_url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir el dashboard");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0c17] pt-28 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  const connected = status?.charges_enabled;
  const started = status?.has_account && !connected;

  return (
    <div className="min-h-screen bg-[#0f0c17] pt-24 pb-16">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-1 ring-white/10">
              <Wallet className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                Cobros y pagos
              </h1>
              <p className="text-gray-500 text-sm mt-1">Configura tu cuenta para recibir reservas</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* STATUS CARD */}
        {connected ? (
          <ConnectedCard
            onOpenDashboard={openDashboard}
            actionLoading={actionLoading}
            status={status}
          />
        ) : started ? (
          <StartedCard
            onContinue={startOnboarding}
            onRefresh={refreshFromStripe}
            actionLoading={actionLoading}
          />
        ) : (
          <NotStartedCard onStart={startOnboarding} actionLoading={actionLoading} />
        )}

        {/* HOW IT WORKS */}
        <div className="mt-10">
          <h2 className="text-white font-semibold text-lg mb-5">Cómo funciona</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <InfoCard
              icon={Shield}
              iconColor="from-emerald-500 to-teal-600"
              title="Seguro"
              text="Stripe gestiona los datos bancarios. Nosotros nunca los vemos."
            />
            <InfoCard
              icon={Percent}
              iconColor="from-violet-500 to-purple-600"
              title="Tasa 10%"
              text="Plazafy cobra una tasa de servicio del 10% al inquilino, tú recibes el 100% del precio."
            />
            <InfoCard
              icon={CreditCard}
              iconColor="from-amber-500 to-orange-600"
              title="Pagos directos"
              text="El dinero de cada reserva llega a tu cuenta bancaria, sin intermediarios."
            />
          </div>
        </div>

        {/* HELP */}
        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm">
            ¿Dudas?{" "}
            <Link to="/ayuda" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
              Visita el Centro de Ayuda
            </Link>{" "}
            o escríbenos a{" "}
            <a href="mailto:hola@plazafy.es" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
              hola@plazafy.es
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function NotStartedCard({ onStart, actionLoading }) {
  return (
    <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-2xl p-5 sm:p-8">
      <div className="flex items-start gap-3 sm:gap-4 mb-5">
        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
          <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-white text-base sm:text-lg font-semibold">Configura tus cobros</h2>
          <p className="text-gray-400 text-xs sm:text-sm mt-1 leading-relaxed">
            Para publicar plazas y recibir reservas necesitas conectar tu cuenta con Stripe. Tarda
            menos de 3 minutos y solo lo haces una vez.
          </p>
        </div>
      </div>
      <button
        onClick={onStart}
        disabled={actionLoading}
        className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="stripe-connect-start-btn"
      >
        {actionLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <CreditCard className="w-4 h-4" />
        )}
        Conectar con Stripe
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </button>
    </div>
  );
}

function StartedCard({ onContinue, onRefresh, actionLoading }) {
  return (
    <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-2xl p-5 sm:p-8">
      <div className="flex items-start gap-3 sm:gap-4 mb-5">
        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
          <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-white text-base sm:text-lg font-semibold">Onboarding pendiente</h2>
          <p className="text-gray-400 text-xs sm:text-sm mt-1 leading-relaxed">
            Empezaste el proceso pero faltan datos. Continúa donde lo dejaste para terminar de
            activar tus cobros.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2.5">
        <button
          onClick={onContinue}
          disabled={actionLoading}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-200 disabled:opacity-60"
          data-testid="stripe-connect-continue-btn"
        >
          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Continuar onboarding
        </button>
        <button
          onClick={onRefresh}
          disabled={actionLoading}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white transition-all duration-200 border border-white/10 hover:border-white/20"
        >
          Ya lo completé, refrescar estado
        </button>
      </div>
    </div>
  );
}

function ConnectedCard({ onOpenDashboard, actionLoading }) {
  return (
    <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/20 rounded-2xl p-5 sm:p-8">
      <div className="flex items-start gap-3 sm:gap-4 mb-5">
        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
          <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-white text-base sm:text-lg font-semibold">Cobros activos</h2>
          <p className="text-gray-400 text-xs sm:text-sm mt-1 leading-relaxed">
            Tu cuenta está conectada. Los pagos de tus reservas llegarán automáticamente a tu cuenta
            bancaria registrada en Stripe.
          </p>
        </div>
      </div>
      <button
        onClick={onOpenDashboard}
        disabled={actionLoading}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60"
        data-testid="stripe-connect-dashboard-btn"
      >
        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
        Abrir mi dashboard Stripe
      </button>
    </div>
  );
}

function InfoCard({ icon: Icon, iconColor, title, text }) {
  return (
    <div className="bg-[#15121e] rounded-2xl border border-white/5 p-5">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconColor} flex items-center justify-center mb-3 shadow-md`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <h3 className="text-white font-medium text-sm">{title}</h3>
      <p className="text-gray-500 text-xs mt-1 leading-relaxed">{text}</p>
    </div>
  );
}
